import React, { memo } from 'react';
import type { StepNode } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';

import { Image } from '../../shared/Image';
import { Text } from '../../shared/Text';
import { OptionsList } from '../../shared/OptionsList';
import { ActionBar } from '../../shared/ActionBar';
import { Background } from '../../shared/Background';
import { HeaderBar } from '../../shared/HeaderBar';
import styles from './HeroList.module.css';

export interface HeroListProps {
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  editMode?: boolean;
  previewMode?: PreviewMode;
}

export const HeroList = memo(function HeroList({
  step,
  locale,
  variables,
  resolvedScreenValues,
  onAction,
  onEvent: _onEvent,
  editMode = false,
  previewMode,
}: HeroListProps): React.ReactElement {
  const { content } = step;

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;

  return (
    <div
      className={`${styles.container}${previewMode === 'live' ? ` ${styles.liveMode}` : ''}${editMode ? ' screen-view--edit-mode' : ''}`}
      data-testid="view-hero-list"
    >

      <Background
        backdrop={content?.canvas?.background}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      <HeaderBar
        header={content?.header}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.header` : undefined}
      />

      <div className={`${styles.content} ${styles.xPaddings}`}>
        <div className={styles.imageTitle}>
          <div className={styles.imageWrapper}>
            <Image
              image={content?.hero}
              locale={locale}
              alt="Hero image"
              editPath={ep ? `${ep}.hero` : undefined}
              previewMode={previewMode}
            />
          </div>
          <Text
            label={content?.copy?.title}
            locale={locale}
            variables={variables}
            resolvedScreenValues={resolvedScreenValues}
            editPath={ep ? `${ep}.copy.title` : undefined}
          />
        </div>

        <Text
          label={content?.copy?.subtitle}
          locale={locale}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          editPath={ep ? `${ep}.copy.subtitle` : undefined}
        />

        <div className={styles.listContainer}>
          <OptionsList
            options={content?.options}
            locale={locale}
            variables={variables}
            editPath={ep ? `${ep}.options` : undefined}
            previewMode={previewMode}
            step={step}
          />
        </div>
      </div>

      <ActionBar
        actionBar={content?.actionBar}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.actionBar` : undefined}
      />
    </div>
  );
});

HeroList.displayName = 'HeroList';
